import { ClaimChanges } from '@/components/claim-management/claim-details/types'
import { toastError, toastSuccess } from '@/components/toast/toast'
import { useAuthContext } from '@/hooks/use-auth-context'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from '@tanstack/react-query'
import axios from 'axios'
import { useRouter } from 'next/navigation'
import {
  archiveClaims,
  createClaim,
  deleteClaim,
  getClaim,
  restoreArchivedClaims,
  submitClaim,
  updateClaim
} from './generated/claim/claim'
import type {
  ClaimDto,
  ClaimStatusTypes,
  CreateClaimRequest
} from './generated/model'
import { queryKeys } from './query-keys'
import { GridSelection } from '@/components/AGGrid/selection/grid-selection'

export const useCreateClaim = () => {
  return useMutation({
    mutationFn: async (request: CreateClaimRequest) => {
      // Calls the API directly: creation is authorized server-side from the authenticated user, and
      // the claim's distributor comes from that user's organization rather than the request — the BFF
      // route used to inject it from the session, which is why the payload no longer carries either it
      // or a permission list.
      // validateStatus lets the caller branch on 207 (partial) and 409 (the selected transactions
      // changed, so no claim was made) rather than catching a rejection.
      const response = await createClaim(request, {
        validateStatus: () => true
      })
      return response
    },
    onError: (error) => {
      console.error(error)
    }
  })
}

export const useGetClaim = (
  claimID?: string,
  navigateOn404: boolean = false,
  options: { refetchOnMount?: boolean } = {}
): UseQueryResult<ClaimDto> => {
  const { session } = useAuthContext()
  const router = useRouter()

  return useQuery({
    queryKey: queryKeys.claims.id(claimID),
    refetchOnMount: options.refetchOnMount,
    queryFn: async () => {
      try {
        // Calls the API directly: the claim's partnership is authorized server-side from the
        // authenticated user, so there is no permission payload for a BFF route to inject.
        const response = await getClaim(claimID!)

        return response.data
      } catch (error) {
        // axios rejects on a non-2xx rather than returning it, so the not-found branch reads the
        // error's status — the BFF path this replaced could inspect response.status directly. The API
        // also answers 404 (not 403) for a claim outside the caller's organizations, so an
        // unauthorized read lands here and navigates away exactly as a missing claim does.
        if (
          navigateOn404 &&
          axios.isAxiosError(error) &&
          error.response?.status === 404
        ) {
          toastError(`Claim not found.`)
          router.push('/claim-management/claims')
          throw new Error('Claim not found.', { cause: error })
        }

        throw new Error(`An unexpected error occurred.`, { cause: error })
      }
    },
    enabled: !!session && !!claimID
  })
}

export const useUpdateClaim = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      claimID,
      request
    }: {
      claimID: string
      request: ClaimChanges
    }) => {
      // Calls the API directly: the API authorizes the update from the authenticated user and
      // resolves which side of the partnership owns the requested transition, so there is no
      // permission payload for a BFF route to inject. The camelCase -> PascalCase mapping the route
      // used to perform happens here instead, and the API returns the claim unwrapped rather than
      // under a `claim` key.
      const response = await updateClaim(claimID, {
        ClaimName: request.name,
        Status: request.status,
        StatusText: request.statusText,
        DebitMemo: request.debitMemo ?? null
      })
      return response.data
    },
    onSuccess: (claim) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.claims.id(claim.ID)
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.claims.history(claim.ID)
      })
    },
    onError: (error) => {
      console.error(error)
    }
  })
}

/** Reads a claim's current status straight from the API, bypassing the query cache. */
export const fetchClaimStatus = async (
  claimID: string
): Promise<ClaimStatusTypes> => {
  const response = await getClaim(claimID)
  return response.data.Status
}

/**
 * Atomically submit a claim to its manufacturer (DEV-956). Calls the C# API directly with the user's
 * token (new/BEFE model): the API authorizes the caller from the user context, performs the
 * Review→Submitted transition server-side, transforms the claim in-process,
 * delivers it over SFTP, and owns its own outcome notifications. The optional debit memo is persisted
 * as part of the transition — one call replaces the previous update-then-submit sequence.
 */
export const useSubmitClaim = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      claimID,
      debitMemo
    }: {
      claimID: string
      debitMemo?: string
    }) => {
      // The body is the debit memo as a plain JSON string (e.g. "MEMO123"), or JSON null to leave
      // the memo unchanged — the endpoint takes a raw string, not a wrapper object.
      const response = await submitClaim(
        claimID,
        JSON.stringify(debitMemo ?? null)
      )
      return response.data
    },
    // Refresh on failure too: a failed submission can put the claim back in Review (DEV-1687).
    onSettled: (_data, _error, { claimID }) => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.claims.id(claimID)
      })
      queryClient.invalidateQueries({
        queryKey: queryKeys.claims.history(claimID)
      })
    },
    onError: (error) => {
      console.error(error)
    }
  })
}

export const useDeleteClaim = () => {
  const { session } = useAuthContext()
  const router = useRouter()

  return useMutation({
    mutationFn: async (claimID: string) => {
      // Calls the API directly: deletion is authorized server-side from the authenticated user, so
      // there is no permission payload for a BFF route to inject — and with it gone the endpoint
      // takes no body at all. Answers 204, so there is nothing to unwrap.
      const response = await deleteClaim(claimID)
      return response.data
    },
    onSuccess: () => {
      toastSuccess('Successfully deleted the claim.')
      router.push('/claim-management/claims')
    },
    onError: (error) => {
      console.error(error)
      toastError(`An error occurred while deleting the claim.`)
    }
  })
}

/**
 * The message for a failed archive or restore. A 400 or 409 carries the API's own message: a filter
 * it cannot apply, or that the matching claims changed since the user selected them (DEV-1638).
 */
const claimArchivalErrorMessage = (
  error: unknown,
  verb: 'archive' | 'restore'
) => {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status
    const detail = error.response?.data?.detail
    if ((status === 400 || status === 409) && typeof detail === 'string') {
      return detail
    }
    if (error.response?.data?.error) {
      return error.response.data.error as string
    }
    if (status === 403) {
      return `You do not have permission to ${verb} these claims.`
    }
    if (status === 404) {
      return `One or more selected claims could not be ${verb}d.`
    }
  }
  return `An unexpected error occurred while ${verb === 'archive' ? 'archiving' : 'restoring'} claims.`
}

const claimsText = (count: number) => `${count} claim${count === 1 ? '' : 's'}`

export const useArchiveClaims = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (selection: GridSelection) => {
      // Archives the claims and cascades to every transaction on them; statuses are untouched.
      // Authorization is server-side: the caller must hold Manage Claim and be the distributor
      // on every selected claim, or the whole request is rejected. A select-all is resolved in the
      // caller's current partnership and must match the count the user saw (DEV-1638).
      const response = await archiveClaims({ Selection: selection })
      return response.data
    },
    onSuccess: (archived) => {
      const count = archived.length
      toastSuccess(
        `Successfully archived ${claimsText(count)} and moved ${count === 1 ? 'its' : 'their'} transactions to the transaction archive`
      )
      queryClient.invalidateQueries({ queryKey: ['claims'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: (error) => {
      toastError(claimArchivalErrorMessage(error, 'archive'))
    }
  })
}

export const useRestoreClaims = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (selection: GridSelection) => {
      // Restores the claims and every transaction on them. Nothing is re-queued for
      // validation — restored claimed transactions keep their claim-derived state.
      const response = await restoreArchivedClaims({ Selection: selection })
      return response.data
    },
    onSuccess: (restored) => {
      const count = restored.length
      toastSuccess(
        `Successfully restored ${claimsText(count)} and ${count === 1 ? 'its' : 'their'} transactions`
      )
      queryClient.invalidateQueries({ queryKey: ['claims'] })
      queryClient.invalidateQueries({ queryKey: ['transactions'] })
    },
    onError: (error) => {
      toastError(claimArchivalErrorMessage(error, 'restore'))
    }
  })
}
